import * as React from 'react'
import { requestErrorMessage, isApiError } from '@/helpers/apiClient'
import { Callout } from '@/components/ui/callout'
import './publicSheet.css'

/**
 * Keep crawlers off a page that answers to anyone holding a link.
 *
 * The third of three layers — `robots.txt` asks, the `X-Robots-Tag` header
 * insists, and this catches a crawler that runs the app rather than reading
 * either. Removed on unmount so it never leaks onto a signed-in page.
 */
function useNoIndex(): void {
  React.useEffect(() => {
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex, nofollow'
    document.head.appendChild(meta)
    return () => meta.remove()
  }, [])
}

/**
 * The frame for both public pages.
 *
 * Deliberately outside `AppLayout`: no nav, no venue switcher, no sign-in. The
 * reader will never have an account, and a page offering them a login box is a
 * page that looks broken to the one person it was built for.
 */
export function PublicShell({
  venueName,
  children,
}: {
  venueName?: string
  children: React.ReactNode
}): React.ReactElement {
  useNoIndex()
  return (
    <div className="min-h-screen bg-page">
      {/* `public-sheet` is what the print stylesheet hangs off — see publicSheet.css. */}
      <div className="public-sheet mx-auto w-full max-w-3xl space-y-6 px-4 py-8">
        {/* The page's one eyebrow (Plan View allows one a screen): whose
            schedule this is, above what it is. */}
        {venueName === undefined ? null : <p className="font-eyebrow m-0">{venueName}</p>}
        {children}
      </div>
    </div>
  )
}

/**
 * Why the page is not showing anything.
 *
 * Every refusal here is a sentence the API wrote for a person to read: a link
 * that has been replaced, one that never existed, an event outside the window.
 * A crew member who gets a bare "not found" concludes the app is broken and
 * telephones somebody, so the message is passed through rather than replaced
 * with a status code.
 */
export function PublicProblem({ error }: { error: Error }): React.ReactElement {
  const known = isApiError(error) && error.body.message !== undefined
  return (
    <PublicShell>
      {/* Honey, not red: nothing is broken on the reader's side, and the fix is
          a new link from the venue — the fact, then what it means. */}
      <Callout tone="attention" title={known ? requestErrorMessage(error) : 'This page could not be loaded.'}>
        {known
          ? 'Nothing is wrong with your device — the venue can send you a working link.'
          : 'Check your connection and try again, or ask the venue for the current link.'}
      </Callout>
    </PublicShell>
  )
}
